/**
 * ProcessAnalyticsPage - Process execution analytics dashboard
 *
 * Surfaces the ProcessAnalyticsDashboard from @ferp/workflow-forms for admin/architect users.
 * Provides insights into process execution patterns, cycle times, and efficiency metrics.
 *
 * Route: /admin/process-analytics
 */

import { useState } from 'react';
import { Container, Title, Group, Badge, Text, ThemeIcon, Select, Stack, Alert } from '@mantine/core';
import { IconChartArrows, IconAlertCircle } from '@tabler/icons-react';

import { ProcessAnalyticsDashboard } from '@ferp/workflow-forms';
import { useAuth } from '@/auth/AuthContext';
import { useListWorkflowsQuery } from '@/generated/graphql';

export function ProcessAnalyticsPage() {
  const { tenant } = useAuth();
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(null);
  const { data: workflowsData, loading: workflowsLoading } = useListWorkflowsQuery({
    variables: { limit: 100 },
  });

  const workflows = workflowsData?.listWorkflows?.items ?? [];
  const selectedWorkflow = workflows.find(w => w.id === selectedWorkflowId);

  return (
    <Container size="xl" py="md">
      <Group justify="space-between" mb="lg">
        <Group gap="sm">
          <ThemeIcon size="lg" variant="light" color="grape">
            <IconChartArrows size={20} />
          </ThemeIcon>
          <div>
            <Title order={3}>Process Analytics</Title>
            <Text size="sm" c="dimmed">
              Process execution patterns, cycle times, and efficiency metrics
            </Text>
          </div>
        </Group>
        {tenant && (
          <Badge variant="light" color="gray">
            {tenant.name}
          </Badge>
        )}
      </Group>

      <Stack gap="md">
        <Select
          label="Select Workflow"
          placeholder="Choose a workflow to analyze"
          data={workflows.map(w => ({
            value: w.id,
            label: w.name || w.id
          }))}
          value={selectedWorkflowId}
          onChange={setSelectedWorkflowId}
          searchable
          clearable
          disabled={workflowsLoading}
        />

        {selectedWorkflowId && selectedWorkflow ? (
          <ProcessAnalyticsDashboard
            workflowId={selectedWorkflowId}
            workflowName={selectedWorkflow.name || selectedWorkflowId}
            timeRange={{
              start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
              end: new Date()
            }}
          />
        ) : (
          <Alert icon={<IconAlertCircle size={16} />} color="blue">
            Select a workflow above to view process analytics.
          </Alert>
        )}
      </Stack>
    </Container>
  );
}

export default ProcessAnalyticsPage;
